export { ErrorBoundary } from '@fhs/routing/src/components/page-dev-error-boundary';

import { Stack, useLocalSearchParams } from 'expo-router';
import { Suspense } from 'react';

import { useSuspenseQuery } from '@fhs/client';
import { Flex } from '@fhs/ui';
import LoadingAnimation from '@fhs-legacy/frontend/src/components/loading-animation';
import LoadingContainer from '@fhs-legacy/frontend/src/components/loading-animation/loading-container';
import NotFound from '@fhs-legacy/frontend/src/pages/not-found';
import { RenderCurrentStaticPage } from '@fhs-legacy/frontend/src/pages/static/render-current-static-page';
import { useNetworkContext } from '@fhs-legacy/frontend/src/state/network';

const useStaticPageQuery = (staticPageSlug: string) => {
  const { groq } = useNetworkContext().sanityEndpoints;

  return useSuspenseQuery({
    queryKey: ['staticPage', staticPageSlug],
    queryFn: async ({ signal }) => {
      const sanityEndpointUrl = new URL(groq);
      sanityEndpointUrl.searchParams.set(
        'query',
        `
          *[_type == 'staticPage' && coalesce(localePath.en.current, path.current) == "${staticPageSlug}"][0] {
              ...,
              "widgets": widgets[]{
                _type == 'callToActionWidget' => {
                  ...,
                  "callToActionContent": callToActionContent[]{
                    _type == 'callToAction' => {
                      ...,
                      image {
                        ...,
                        en {
                          ...,
                          asset->
                        }
                      }
                    }
                  }

                },
                _type == 'imageWidget' => {
                  ...,
                  image {
                    ...,
                    en {
                      ...,
                      asset->
                    }
                  }
                },
                _type != 'imageWidget' && _type != 'callToActionWidget' => @
              }
            }
        `
      );

      const response = await fetch(sanityEndpointUrl, { signal });
      const { result } = await response.json();

      return result as Record<string, any>;
    },
  });
};

function StaticPage() {
  const { staticPage } = useLocalSearchParams<{ staticPage: string }>();

  const { data: staticPageData } = useStaticPageQuery(staticPage);

  if (!Object.keys(staticPageData).length) {
    return <NotFound />;
  }

  return (
    <>
      <Stack.Screen options={{ title: staticPageData.title }} />
      <Flex testID="static-page" key={staticPage}>
        <Flex>
          <RenderCurrentStaticPage currentPage={staticPageData} />
        </Flex>
      </Flex>
    </>
  );
}

export default () => {
  return (
    <Suspense
      fallback={
        <LoadingContainer>
          <LoadingAnimation />
        </LoadingContainer>
      }
    >
      <StaticPage />
    </Suspense>
  );
};
